import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { TabsContent } from '@/components/ui/tabs';
import { useAuth } from '@/hooks/useAuth';
import { useSubscriptionAccess } from '@/hooks/useSubscriptionAccess';
import { useFamilyDetails } from '@/hooks/useFamilyDetails';
import HiveShareFamilyDetail from '@/components/families/hiveshare/HiveShareFamilyDetail';
import { useParentActions } from '@/hooks/family/useParentActions';
import FamilyDetailLayout from '@/components/families/detail/FamilyDetailLayout';
import FamilyOverviewTab from '@/components/families/detail/FamilyOverviewTab';
import FamilyParentsTab from '@/components/families/detail/FamilyParentsTab';
import FamilyStudentsTab from '@/components/families/detail/FamilyStudentsTab';
import FamilyBillingTab from '@/components/families/detail/FamilyBillingTab';
import { useFamilyDetailActions } from '@/hooks/useFamilyDetailActions';
import { Parent, Student } from '@/types/models';
import EditStudentDialog from '@/components/students/EditStudentDialog';
import { toast } from 'sonner';

const FamilyDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { organizationId, userRole } = useAuth();
  const { isHiveShareOnly, loading: subscriptionLoading } = useSubscriptionAccess();
  const familyId = id || '';
  
  // Student edit state - MUST be called before any conditional returns
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [isEditStudentDialogOpen, setIsEditStudentDialogOpen] = useState(false);

  // Get family details using the hook
  const {
    family,
    parents,
    students,
    isAddingParent,
    isAddingStudent,
    isDeleting,
    handleAddParent,
    handleAddStudent,
    handleDeleteFamily,
    setParents,
    setFamily,
    setStudents
  } = useFamilyDetails(familyId);

  // Get parent action handlers
  const {
    editingParent,
    setEditingParent,
    isEditParentDialogOpen,
    setIsEditParentDialogOpen,
    isDeletingParent,
    handleSendInvite,
    handleEditParent,
    handleParentUpdated,
    handleDeleteParent,
    handleToggleParentActiveStatus
  } = useParentActions();

  // Use the new actions hook
  const {
    activeTab,
    setActiveTab,
    isAddParentDialogOpen,
    setIsAddParentDialogOpen,
    isAddStudentDialogOpen,
    setIsAddStudentDialogOpen,
    handleBackClick,
    handleAddParentClick,
    handleAddStudentClick,
    handleParentAdded,
    handleStudentAdded,
    handleDeleteFamilyClick,
    handleToggleActiveStatus,
    handleUpdateBillingStatus
  } = useFamilyDetailActions(
    familyId,
    organizationId,
    family,
    setFamily,
    parents,
    setParents,
    handleAddParent,
    handleAddStudent,
    handleDeleteFamily,
    setEditingParent,
    setIsEditParentDialogOpen,
    setEditingStudent,
    setIsEditStudentDialogOpen
  );

  // Handler for parent updates
  const handleParentUpdate = (parentId: string, updatedData: any) => {
    handleParentUpdated(parentId, updatedData, parents, setParents);
  };

  // Handler for parent invites
  const handleSendParentInvite = (parentId: string) => {
    handleSendInvite(parentId, parents, setParents);
  };

  // Handler for deleting a parent
  const handleInitiateDeleteParent = (parent: Parent) => {
    handleDeleteParent(parent.id, parents, setParents);
  };
  
  // Async wrapper to match expected Promise<void> type
  const handleDeleteParentAsync = async (parent: Parent): Promise<void> => {
    handleInitiateDeleteParent(parent);
  };
  
  // Handler for toggling parent active status
  const handleToggleParentStatus = (parent: Parent, isActive: boolean) => {
    handleToggleParentActiveStatus(parent, isActive, parents, setParents);
  };
  
  // Handler for editing a student
  const handleEditStudent = (student: Student) => {
    console.log("FamilyDetail: Edit student called directly for:", student.id, student.first_name);
    setEditingStudent(student);
    setIsEditStudentDialogOpen(true);
  };
  
  // Handler for updating a student
  const handleStudentUpdated = (updatedStudent: Student) => {
    // Update the student in the local state
    setStudents(prevStudents => 
      prevStudents.map(s => s.id === updatedStudent.id ? updatedStudent : s)
    );
    
    toast.success(`${updatedStudent.first_name} ${updatedStudent.last_name} updated successfully`);
    setIsEditStudentDialogOpen(false);
    setEditingStudent(null);
  };

  // Show loading while determining subscription type to prevent UI flash
  if (subscriptionLoading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  // Return simplified HiveShare interface for HiveShare-only users
  // MUST be after ALL hooks to avoid React error #300
  if (isHiveShareOnly) {
    return <HiveShareFamilyDetail />;
  }

  return (
    <FamilyDetailLayout
      family={family}
      parents={parents}
      students={students}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      handleBackClick={handleBackClick}
      handleAddParentClick={handleAddParentClick}
      handleAddStudentClick={handleAddStudentClick}
      handleParentAdded={handleParentAdded}
      handleStudentAdded={handleStudentAdded}
      isAddParentDialogOpen={isAddParentDialogOpen}
      setIsAddParentDialogOpen={setIsAddParentDialogOpen}
      isAddStudentDialogOpen={isAddStudentDialogOpen}
      setIsAddStudentDialogOpen={setIsAddStudentDialogOpen}
      isAddingParent={isAddingParent}
      isAddingStudent={isAddingStudent}
      editingParent={editingParent}
      isEditParentDialogOpen={isEditParentDialogOpen}
      setIsEditParentDialogOpen={setIsEditParentDialogOpen}
      handleParentUpdate={handleParentUpdate}
      handleDeleteParent={handleDeleteParentAsync}
      familyId={familyId}
    >
      <TabsContent value="overview">
        <FamilyOverviewTab 
          family={family!}
          parents={parents}
          students={students}
          onAddParent={handleAddParentClick}
          onAddStudent={handleAddStudentClick}
          onSendParentInvite={handleSendParentInvite}
          onEditParent={handleEditParent}
          onEditStudent={handleEditStudent}
          onDeleteParent={handleDeleteParentAsync}
          onDeleteFamily={handleDeleteFamilyClick}
          onToggleActiveStatus={handleToggleActiveStatus}
          onToggleParentActiveStatus={handleToggleParentStatus}
          isDeleting={isDeleting}
        />
      </TabsContent>
      
      <TabsContent value="parents">
        <FamilyParentsTab 
          parents={parents}
          onAddParent={handleAddParentClick}
          onSendInvite={handleSendParentInvite}
          onEditParent={handleEditParent}
          onDeleteParent={handleDeleteParentAsync}
          onToggleParentActiveStatus={handleToggleParentStatus}
        />
      </TabsContent>
      
      <TabsContent value="students">
        <FamilyStudentsTab 
          students={students}
          onAddStudent={handleAddStudentClick}
          onEditStudent={handleEditStudent}
        />
      </TabsContent>
      
      <TabsContent value="billing">
        <FamilyBillingTab 
          family={family!}
          onUpdateBillingStatus={handleUpdateBillingStatus}
        />
      </TabsContent>
      
      {/* Dialog for editing a student */}
      {editingStudent && (
        <EditStudentDialog
          student={editingStudent}
          open={isEditStudentDialogOpen}
          onOpenChange={setIsEditStudentDialogOpen}
          onUpdated={handleStudentUpdated}
        />
      )}
    </FamilyDetailLayout>
  );
};

export default FamilyDetail;
